/**
 * /pressure-testing/jobs — the test job register (fieldiq-api#915
 * `GET /pressure-testing/jobs`).
 *
 * STATUS IS THE API'S, and so is every tally on a row: "passed of total" is
 * the job's `test_counts` as returned, never counted here from the tests.
 *
 * FILTERING: `status` and `q` are query params the API reads. "All" is the
 * unfiltered list WITHOUT cancelled jobs — a cancelled job is out of play, and
 * it has its own chip. Hiding it is presentation, not a second answer.
 *
 * THE COUNTS ARE TALLIES OF THE UNFILTERED LIST, honest only because no PT
 * endpoint returns a job count and the list is unpaginated (the API answers
 * every job). If it is ever paginated, these must come from the API.
 *   - Awaiting issue (PT-4): the length of `GET /certificates?status=draft` —
 *     the same list the Certificates page's "Awaiting issue" chip draws, also
 *     unpaginated. It took slot one from "This week" (jobs scheduled this
 *     week): the band holds four stats at most, and slot one is the column the
 *     hero circle never reaches, so the third and fourth keep the copy their
 *     clearance was measured with.
 *   - Complete this week: complete jobs whose completed_at falls in the
 *     Mon–Sun week of the TENANT's day (meta.today),
 *     in the tenant's zone (meta.time_zone).
 *   - Need calibration: the length of the API's alerts list (never calibrated,
 *     overdue, due soon) — the same list /pressure-testing/alerts draws.
 *
 * WRITES: New test job, for PT_DISPATCH_ROLES (admin, office_staff, supervisor,
 * dispatcher).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, ChevronRight, FileClock, Loader2, Plus, PlayCircle, Search } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { usePtCalibrationAlerts, usePtCertificates, usePtJobs } from '../../hooks/usePressureTesting';
import { formatCalendarDate, instantTenantDay } from '../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canDispatchPt, ptJobStatusSkin, ptWeekBounds } from '../../lib/pressure-testing';
import { PT_JOB_STATUSES, type PtJob, type PtJobStatus } from '../../types/pressure-testing';
import { NewTestJobDialog } from './PtJobDialogs';
import { PtChip, PtEmpty, PtPanel } from './PtBits';

const isJobStatus = (v: string | null): v is PtJobStatus => !!v && (PT_JOB_STATUSES as readonly string[]).includes(v);

const day = (value: string | null | undefined) => formatCalendarDate(value) ?? '—';

/** The job status as a saturated badge, dark ink on the fill. */
export function PtJobStatusBadge({ status }: { status: string | null | undefined }) {
  const skin = ptJobStatusSkin(status);
  return (
    <span
      data-status={status ?? ''}
      className={`inline-flex w-fit items-center whitespace-nowrap rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${skin.fill}`}
    >
      {skin.label}
    </span>
  );
}

/** "2 of 3" from the API's tallies; "No tests" for a real zero; `—` when the tallies did not arrive. */
export function testsPassedText(job: Pick<PtJob, 'test_counts'>): string {
  const c = job.test_counts;
  if (!c || typeof c.total !== 'number' || typeof c.pass !== 'number') return '—';
  return c.total === 0 ? 'No tests' : `${c.pass} of ${c.total}`;
}

const CHIPS: { key: '' | PtJobStatus; label: string }[] = [
  { key: '', label: 'All' },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'active', label: 'Active' },
  { key: 'complete', label: 'Complete' },
  { key: 'cancelled', label: 'Cancelled' },
];

export default function TestJobsScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusParam = searchParams.get('status');
  const status = isJobStatus(statusParam) ? statusParam : '';
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const params = useMemo(() => ({ ...(status && { status }), ...(q && { q }) }), [status, q]);
  const listQuery = usePtJobs(params);
  // The unfiltered list, for the counts. The same query as `listQuery` when
  // nothing is filtered, so React Query serves both from one request.
  const allQuery = usePtJobs();
  const alertsQuery = usePtCalibrationAlerts();
  const draftsQuery = usePtCertificates({ status: 'draft' });

  const meta = allQuery.data?.meta ?? listQuery.data?.meta;
  const listFailure = queryLoadFailure(listQuery);
  // "All" hides cancelled; the Cancelled chip is the way to them.
  const jobs = (listQuery.data?.data ?? []).filter((j) => !!status || j.status !== 'cancelled');
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data?.data;
  const countsLoading = allQuery.isLoading;
  const week = ptWeekBounds(meta?.today);
  const canCreate = canDispatchPt(user?.role);

  const tally = (pick: (j: PtJob) => boolean) => (all ? all.filter(pick).length : undefined);
  const inWeek = (d: string | null | undefined) => !!week && !!d && d >= week.from && d <= week.to;
  const completeThisWeek =
    week && meta?.time_zone
      ? tally((j) => j.status === 'complete' && inWeek(instantTenantDay(j.completed_at, meta.time_zone)))
      : undefined;
  const alertsFailed = !!queryLoadFailure(alertsQuery);
  const needCal = alertsQuery.isLoading || alertsFailed ? undefined : alertsQuery.data?.data.length;
  const draftsFailed = !!queryLoadFailure(draftsQuery);
  const awaitingIssue = draftsQuery.isLoading || draftsFailed ? undefined : draftsQuery.data?.data.length;

  const chipCount = (key: '' | PtJobStatus) =>
    countsLoading ? undefined : displayValue(key ? tally((j) => j.status === key) : tally((j) => j.status !== 'cancelled'));

  const setStatus = (next: string) => {
    const nextParams = new URLSearchParams(searchParams);
    if (next) nextParams.set('status', next);
    else nextParams.delete('status');
    setSearchParams(nextParams, { replace: true });
  };

  const open = (id: string) => navigate(`/pressure-testing/jobs/${id}`);
  const rowKeys = (id: string) => (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      open(id);
    }
  };

  const filtered = !!(status || q);

  return (
    <div data-testid="pt-jobs-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Pressure testing"
        title="Test Jobs"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canCreate ? (
            <button type="button" onClick={() => setCreating(true)} className="fi-action fi-action--standard fi-action--primary">
              <Plus className="size-4" aria-hidden="true" />
              New test job
            </button>
          ) : undefined
        }
        stats={[
          {
            icon: <FileClock className="size-4" aria-hidden="true" />,
            label: 'Awaiting issue',
            sub: draftsFailed ? 'Could not load' : 'Certificates',
            tone: 'accent',
            loading: draftsQuery.isLoading,
            value: displayValue(awaitingIssue),
          },
          {
            icon: <PlayCircle className="size-4" aria-hidden="true" />,
            label: 'Active',
            sub: 'In progress',
            tone: 'accent',
            loading: countsLoading,
            value: displayValue(tally((j) => j.status === 'active')),
          },
          {
            icon: <CheckCircle2 className="size-4" aria-hidden="true" />,
            // "Done", not "Complete": the third stat label's ceiling is ~70px
            // (CLAUDE.md band clearance) and "Complete" measured 72. The chip keeps the name.
            label: 'Done',
            sub: 'This week',
            tone: 'success',
            loading: countsLoading,
            value: displayValue(completeThisWeek),
          },
          {
            icon: <AlertTriangle className="size-4" aria-hidden="true" />,
            label: 'Need cal',
            sub: alertsFailed ? 'Could not load' : 'Instruments',
            tone: 'accent',
            loading: alertsQuery.isLoading,
            value: displayValue(needCal),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <div className="scrollbar-hide flex gap-2 overflow-x-auto" role="group" aria-label="Filter by status">
            {CHIPS.map((c) => (
              <PtChip key={c.key || 'all'} label={c.label} active={status === c.key} onClick={() => setStatus(c.key)} count={chipCount(c.key)} />
            ))}
          </div>

          <label className="relative block">
            <span className="sr-only">Search job number, customer, location, UWI, LSD, PO or AFE</span>
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search job number, customer, location, UWI, LSD, PO or AFE"
              className="h-10 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card pl-9 pr-3 text-sm text-white placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
            />
          </label>

          <PtPanel
            eyebrow="Field work"
            title="Test jobs"
            testId="pt-job-register"
            footer={
              !listQuery.isLoading && !listFailure && jobs.length > 0
                ? `${jobs.length} job${jobs.length === 1 ? '' : 's'}${!status ? ', cancelled not shown' : ''}`
                : undefined
            }
          >
            {listQuery.isLoading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading test jobs" />
              </div>
            ) : listFailure ? (
              <LoadFailureNotice error={queryFailureError(listQuery)} kind={listFailure} subject="test jobs" />
            ) : jobs.length === 0 ? (
              filtered ? (
                <PtEmpty>No test job matches these filters.</PtEmpty>
              ) : (
                <div className="px-4 py-10 text-center" data-testid="pt-jobs-empty">
                  <p className="text-sm text-fieldiq-text">There are no test jobs.</p>
                  {canCreate && (
                    <button type="button" className="fi-action fi-action--standard fi-action--primary mt-4" onClick={() => setCreating(true)}>
                      <Plus className="size-4" aria-hidden="true" />
                      New test job
                    </button>
                  )}
                </div>
              )
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[1040px] text-left">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                      <tr className="border-l-4 border-l-transparent">
                        <th scope="col" className="px-4 py-3">Job #</th>
                        <th scope="col" className="px-4 py-3">Customer / location</th>
                        <th scope="col" className="px-4 py-3">Scope</th>
                        <th scope="col" className="px-4 py-3">Tech</th>
                        <th scope="col" className="px-4 py-3">Tests passed</th>
                        <th scope="col" className="px-4 py-3">Status</th>
                        <th scope="col" className="px-4 py-3">Date</th>
                        <th scope="col" className="w-[44px] px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {jobs.map((j) => {
                        const cancelled = j.status === 'cancelled';
                        const ink = cancelled ? 'text-fieldiq-muted' : 'text-fieldiq-text';
                        return (
                          <tr
                            key={j.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => open(j.id)}
                            onKeyDown={rowKeys(j.id)}
                            data-testid="pt-job-row"
                            data-cancelled={cancelled || undefined}
                            className={`cursor-pointer border-t border-l-4 border-fieldiq-border outline-none transition hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${
                              ptJobStatusSkin(j.status).rail
                            } ${cancelled ? 'bg-fieldiq-dark/60' : ''}`}
                          >
                            <td className="px-4 py-3">
                              <p className={`font-mono text-sm font-extrabold ${cancelled ? 'text-fieldiq-muted' : 'text-white'}`}>{j.job_number || '—'}</p>
                              <p className="mt-0.5 text-xs text-fieldiq-muted">
                                PO {j.po_number || '—'} · AFE {j.afe_number || '—'}
                              </p>
                            </td>
                            <td className="px-4 py-3">
                              <p className={`text-sm font-semibold ${cancelled ? 'text-fieldiq-muted' : 'text-white'}`}>{j.customer_name || '—'}</p>
                              <p className="mt-0.5 text-xs text-fieldiq-muted">
                                {[j.location_name, j.lsd].filter(Boolean).join(' · ') || '—'}
                              </p>
                            </td>
                            <td className={`max-w-[280px] px-4 py-3 text-sm ${ink}`}>
                              <p className="line-clamp-2">{j.scope || '—'}</p>
                            </td>
                            <td className={`whitespace-nowrap px-4 py-3 text-sm ${ink}`}>{j.technician_name || 'Not assigned'}</td>
                            <td className={`whitespace-nowrap px-4 py-3 font-mono text-sm tabular-nums ${ink}`}>{testsPassedText(j)}</td>
                            <td className="px-4 py-3"><PtJobStatusBadge status={j.status} /></td>
                            <td className={`whitespace-nowrap px-4 py-3 text-sm ${ink}`}>{day(j.scheduled_date)}</td>
                            <td className="px-4 py-3"><ChevronRight className="size-4 text-fieldiq-muted" aria-hidden="true" /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="space-y-3 p-4 md:hidden">
                  {jobs.map((j) => {
                    const cancelled = j.status === 'cancelled';
                    return (
                      <div
                        key={j.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => open(j.id)}
                        onKeyDown={rowKeys(j.id)}
                        className={`cursor-pointer rounded-fi-panel border border-fieldiq-border px-4 py-3 outline-none transition hover:border-fieldiq-muted focus-visible:border-fi-amber focus-visible:bg-fi-amber/10 ${
                          cancelled ? 'bg-fieldiq-dark/60' : 'bg-fieldiq-card'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className={`font-mono text-sm font-extrabold ${cancelled ? 'text-fieldiq-muted' : 'text-white'}`}>{j.job_number || '—'}</p>
                            <p className="mt-0.5 truncate text-xs text-fieldiq-muted">{j.customer_name || '—'}</p>
                          </div>
                          <PtJobStatusBadge status={j.status} />
                        </div>
                        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-fieldiq-text">
                          <span>{day(j.scheduled_date)}</span>
                          <span>{j.technician_name || 'Not assigned'}</span>
                          <span>Tests {testsPassedText(j)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </PtPanel>
        </div>
      </section>

      {creating && (
        <NewTestJobDialog
          open={creating}
          onClose={() => setCreating(false)}
          today={meta?.today ?? null}
          onCreated={(job) => open(job.id)}
        />
      )}
    </div>
  );
}
